import { CollectionsService } from 'src/app/core/services/collections/collections.service';
import { ActiveInspectionSnapshot } from './active-inspection-context.service';
import { UniversalSearchKind, UniversalSearchResult } from './universal-search.models';

const NESTED_KINDS: UniversalSearchKind[] = ['category', 'item', 'action'];

export type RecentAvailability = 'available' | 'gone' | 'unknown';

export function inspectionIdFromCommands(commands: unknown[]): string | null {
  for (let index = 0; index < commands.length - 1; index++) {
    const next = commands[index + 1];
    if (commands[index] === 'inspections' && typeof next === 'string') return next;
  }
  return null;
}

/**
 * Nested recents can be confirmed from the open inspection without a read. `null` means this
 * snapshot does not cover the record, so Firestore still has to be consulted.
 */
export function nestedRecentAvailability(snapshot: ActiveInspectionSnapshot | null, workspaceId: string, recent: UniversalSearchResult): boolean | null {
  if (!NESTED_KINDS.includes(recent.kind)) return null;
  if (!snapshot || snapshot.workspaceId !== workspaceId) return null;

  const inspectionId = inspectionIdFromCommands(recent.commands);
  if (!inspectionId || snapshot.inspectionId !== inspectionId) return null;

  switch (recent.kind) {
    case 'category':
      return snapshot.categories.some((category) => category.id === recent.id);
    case 'item':
      return snapshot.items.some((item) => item.id === recent.id);
    case 'action':
      return snapshot.actions.some((action) => action.id === recent.id);
    default:
      return null;
  }
}

export function documentPathForRecent(collections: CollectionsService, workspaceId: string, recent: UniversalSearchResult): string | null {
  switch (recent.kind) {
    case 'folder':
      return `${collections.foldersCol(workspaceId)}/${recent.id}`;
    case 'inspection':
      return `${collections.inspectionsCol(workspaceId)}/${recent.id}`;
    case 'client':
      return `${collections.clientsCol(workspaceId)}/${recent.id}`;
    case 'site':
      return `${collections.sitesCol(workspaceId)}/${recent.id}`;
    case 'member':
      return `${collections.membersCol(workspaceId)}/${recent.id}`;
    case 'role':
      return `${collections.rolesCol(workspaceId)}/${recent.id}`;
    case 'assignee':
      return `${collections.assigneesCol(workspaceId)}/${recent.id}`;
    case 'template':
      return `${collections.templatesCol(workspaceId)}/${recent.id}`;
    case 'theme':
      return `${collections.themesCol(workspaceId)}/${recent.id}`;
    case 'action':
      return `${collections.actionsCol(workspaceId)}/${recent.id}`;
    case 'category': {
      const inspectionId = inspectionIdFromCommands(recent.commands);
      return inspectionId ? `${collections.inspectionCategoriesCol(workspaceId, inspectionId)}/${recent.id}` : null;
    }
    case 'item': {
      const inspectionId = inspectionIdFromCommands(recent.commands);
      return inspectionId ? `${collections.inspectionItemsCol(workspaceId, inspectionId)}/${recent.id}` : null;
    }
    default:
      return null;
  }
}

export function isRecentDocumentAvailable(kind: UniversalSearchKind, data: Record<string, unknown>): boolean {
  if (kind === 'member') return data['status'] !== 'removed';
  return data['is_deleted'] !== true;
}

export function availabilityFromFirestoreError(error: unknown): RecentAvailability {
  if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: unknown }).code === 'permission-denied') {
    return 'gone';
  }
  return 'unknown';
}
